import { ISizeRuns, ProductQuery } from '@epicuro-next/data/product';
import { ProductListSource } from '@epicuro-next/data/stories';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { IRecommendationTrackSource } from '@epicuro-next/models/recommendation-panels';
import { EcommerceTrackingSource } from '@epicuro-next/platform/tracking';
import { ExtendedBasketAccountAddress } from '@epicuro-next/state/basket-state';
import { Unpacked } from '@epicuro-next/utilities';
import { EntityState } from '@ngrx/entity';
import {
  BasketAccountAddress,
  Maybe,
} from 'graphql-api/baskets-api-interfaces';
import {
  OrderType,
  PrepackUnit,
  PriceData,
  ProductPriceRanges,
  ScaleSets,
} from 'graphql-api/gql-api-interfaces';

export type ProductPrices = { [productId: string]: ProductPriceRanges[] };
export type ProductType = NonNullable<ProductQuery['product']>;
export type PrePackType = NonNullable<Unpacked<ProductType['prepacks']>>;
export type ProductVariant = Unpacked<ProductType['children']> & {
  sizeReferences: string[];
};
export type ProductExtendedVariant = ProductVariant & {
  articleId: string | null;
};
export type ProductVariantEntities = {
  [index: string]: ProductVariant;
};
type ProductVariantType = Unpacked<NonNullable<ProductType['children']>>;
export type ProductSizeType = Unpacked<ProductVariantType['children']>;
export type ProductSizeEntity = ProductSizeType & {
  variantReference: string;
  isNotConsumableInventories: InventoryItem[];
};
export type ProductSizeEntities = {
  [index: string]: ProductSizeEntity;
};
export type ProductPrepackEntities = {
  [index: string]: PrePackType;
};
export type InventoryItem = NonNullable<
  Unpacked<NonNullable<ProductSizeType>['inventoryItems']>
>;
export const DEFAULT_DELIVERY_PERIOD_ID = OrderType.Now;
export type DeliveryItem = {
  /** used for quantity, includes true demand */
  quantity: number;
  /** used for consumed true demand quantity */
  usedTrueDemandQuantity: number;
  note?: string | null;
  /** used to add inventory availability boundaries of requested delivery date */
  availabilityDateFrom?: string;
  /** used to add inventory availability boundaries of requested delivery date */
  availabilityDateTo?: string;
  // if true then user delivery period has lower prio over inventory delivery date
  useInventoryDate?: boolean;
  /** used for bulk order indicate start date */
  consignmentStartDate?: string;
  /** used for bulk order indicate end date */
  consignmentEndDate?: string;
  /** so c-basket knows what to show */
  displayDateFrom?: string | null;
  /** for backend to know which inventories are consumed */
  inventoryId?: string;
};
/**
 * Contains the products that have been added for a given order type
 */
export type DeliveryItems = {
  orderType: OrderType;
  deliveryPeriodType?: DeliveryPeriodType;
  items: {
    [productId: string]: DeliveryItem;
  };
};

export type PrefilledDeliverySizeItems = {
  // goes from top to bottom, where number - quantity of items filled
  [width: string]: {
    [size: string]: number;
  };
};

export type PrefilledDeliverySizeItemsByPeriod = {
  [deliveryPeriod: string]: PrefilledDeliverySizeItems;
};

export type PrefilledDeliverySizeItemsByShop = {
  [deliveryShop: string]: PrefilledDeliverySizeItemsByPeriod;
};

/**
 * Contains already selected quantities by user
 */
export type PrefilledDeliveryItems = {
  category?: string;
  masterQuantities?: PrefilledDeliverySizeItems;
  visible?: boolean;
  items?: PrefilledDeliverySizeItemsByShop;
};

/** Delivery items the user has entered per each delivery period */
export type DeliveryItemsByPeriod = {
  [deliveryPeriod: string]: DeliveryItems;
};
/** Delivery items the user has entered per each delivery shop */
export type DeliveryItemsByShop = {
  [deliveryShop: string]: DeliveryItemsByPeriod;
};
export type UsedInventoryForPeriod = {
  [inventoryId: string]: number;
};
/** Inventories from which units have been taken. Used to optimize availability for future inventories */
export type UsedInventories = {
  [deliveryPeriod: string]: UsedInventoryForPeriod;
};

/** Inventories from which units have been taken. Used to optimize availability for future inventories */
export type UsedInventoriesForDeliveryShop = {
  [deliveryShop: string]: UsedInventories;
};

export type ShopDeliveryItemTotals = { [shopId: string]: number };

export type BasketShopCopy = { fromShopId: string; toShopIds: string[] };

export type BasketShopClear = { shopId: string };

export type TotalPrices = {
  wsp: number;
  rrp: number;
};

export enum DisplayByMode {
  Prepack = 'prepack',
  AdvancedPrepack = 'advanced-prepack',
  Size = 'size',
  AdvancedSize = 'advanced-size',
}

export enum DeliveryPeriodType {
  Now = 'NOW',
  Future = 'Future',
  Bulk = 'Bulk',
}

export type BackgroundProductState = Partial<State> & {
  brandId: string;
  productId: string;
  mainImageUrl?: string | null;
  productParentId?: string;
  category?: string;
};

export type CachedState = {
  /** cached is for that account id */
  accountId?: string;
  selectedDeliveryPeriods?: string[];
  /** cached last selected date in the date picker */
  lastSelectedDate?: string;

  memoProductSizes?: { [index: string]: MemoProductSizes };
};

export interface MemoProductSizes {
  selectedScale: string;
  availableScaleSets: Maybe<ScaleSets>;
  materials: ProductExtendedVariant[];
  scaleSetKeys: string[];
  prepacks: MemoPrepack[];
}

export interface MemoPrepack {
  quantities: Pick<PrepackUnit, 'productId' | 'quantity'>[];
  productId: string;
}

export type BulkTypeDeliveryPeriod = {
  from: string;
  to: string;
};

export type DeliveryPeriodsByType<T extends string | BulkTypeDeliveryPeriod> = {
  selectedDeliveryPeriod?: T;
  unavailableDeliveryPeriods?: T[];
  deliveryPeriods?: T[];
};

export type DeliveryPeriodsByPeriodType = {
  /** used to identify which calendar open/select; what tile to highlight; for what calculate inventories */
  currentDeliveryPeriodType?: DeliveryPeriodType;
  // We do not need to store "DeliveryPeriodsByType" for NOW delivery periods
  // just update currentDeliveryPeriodType
  [DeliveryPeriodType.Future]?: DeliveryPeriodsByType<string>;
  [DeliveryPeriodType.Bulk]?: DeliveryPeriodsByType<BulkTypeDeliveryPeriod>;
  [DeliveryPeriodType.Now]?: DeliveryPeriodsByType<string>;
};

export type DeliveryPeriodsByShop = {
  [deliveryShop: string]: DeliveryPeriodsByPeriodType;
};

export type ValidationErrors = {
  [deliveryShop: string]: {
    [deliveryPeriod: string]: string[];
  };
};

export enum PriceType {
  Now = 'now',
  Future = 'future',
}

export type ProductPriceTypes = {
  now: ProductPrices;
  future: ProductPrices;
};

export type ProductPriceData = {
  now: PriceData[];
  future: PriceData[];
};

export type UiEcommerceTrackingSource = {
  sourceType: EcommerceTrackingSource;
  position?: number;
};

/** UI part of CS */
export interface UiState {
  /** Tells if cstrip open or not */
  isOpen: boolean;
  /** Is the config strip data loading */
  loading: boolean;
  /** Shows either cached product is resolved */
  loadedCached: boolean;
  /** Error message if the loading fails */
  error: string | null;
  pricesLoading: boolean;
  /** Prices error message if the loading fails or there are neither now nor future prices  */
  pricesError: string | null;
  /** if the validation is in progress */
  validating: boolean;
  /** the result of the validators to be displayed on the CS */
  validationErrors: ValidationErrors;
  /** all inventories are for NOW or FUTURE orders; it says what type of inventories are buying right now */
  preSelectDisplayByMode: DisplayByMode | null;
  preSelectedOrderType: OrderType | null;
  selectedOrderType: OrderType | null;
  preSelectedDeliveryPeriodType: DeliveryPeriodType | null;
  preSelectedDeliveryPeriod: string | BulkTypeDeliveryPeriod | null;
  /** the initial selected product variant */
  initialSelectedVariant: string | null;
  /** the currently selected product variant */
  selectedVariant: string | null;
  /** the currently selected product size */
  selectedSize: string | null;
  pastedData: boolean;
  displayByMode: DisplayByMode;
  /** track the delivery periods with copied values */
  copiedDeliveryPeriods: string[];
  /** track the shops with copied values */
  copiedShops: string[];
  ecommerceTrackingSource: UiEcommerceTrackingSource | null;
  initialSelectedPrepack: string | null;
  selectedPrepack: string | null;
}

/** Business (Domain) part of CS. Perfectly has to be killed as most as possible following the
 * "things that could be calculated should be calculated"
 */
export interface BusinessState {
  /** Prefilled  quantities by user for certain period */
  prefilledDeliveryItems: PrefilledDeliveryItems;
  /** Products that will be opened by CS itself, for example, by clicking next */
  backgroundProducts: EntityState<BackgroundProductState>;
  /** Contains all information about delivery periods: current selected, current selected type, preselected */
  deliveryPeriods: DeliveryPeriodsByShop;
  nowDeliveryPeriods?: DeliveryPeriodsByShop;
  /** Holds the delivery items the user has entered per each delivery period */
  deliveryItems: DeliveryItemsByShop;
  /** Inventories from which units have been taken. Used to optimize availability for future inventories */
  usedInventories: UsedInventoriesForDeliveryShop;
  /** Keeps track of what items have been modified after the initialization. Cannot use a Set, that's why the value is null */
  modifiedProducts?: {
    [deliveryShopKey: string]: {
      [productId: string]: null;
    };
  };
  /** Tells is it's currently persisting changes */
  persistingChanges: boolean;
  totalQuantity: number;
  totalPrices: TotalPrices;
  /** any changes are made */
  dirty: boolean;
  /** Navigate to next product  */
  navigateToNext: boolean;
  /** Show navigate to next product button */
  showNextButton: boolean;
  /** Skips inventory check if defined  */
  skipInventoryCheck: boolean;
  /** used for tracking */
  source: ProductSource;
  /** to show small version of grid */
  memoProductSizes: { [index: string]: MemoProductSizes };
  /** previous partial CS states */
  cached: CachedState;
  /** CS can support multiple delivery shops active */
  activeDeliveryShops: BasketAccountAddress[];
  /** current active delivery shop tab */
  activeDeliveryShop: BasketAccountAddress;
  /** */
  isSetSourceNeed: boolean;
}

export interface ProductSourceDetails {
  lookBookId?: string[];
  listId?: string[];
  isCommercial?: boolean;
  isPowerList?: boolean;
  listType?: string;
  basketId?: string[];
  recommendationPanel?: IRecommendationTrackSource;
  query?: string;
  // used in additionalUiDetails for prevent overriden 'recommendation' with 'search'
  searchParams?: Record<string, string> | null;
  // used for secondary navigation link tracking
  linkUrl?: string;
  linkText?: string;
  // used for story navigation with ProductListSource.VARIANT, ProductListSource.LIST
  productId?: string;
  suggestedFilter?: { fieldId: string; value: string };
  // horizontal product basket search
  isTable?: boolean;
}

export interface ProductSource {
  type: BasketItemSource | ProductListSource;
  details: ProductSourceDetails;
  // not saved to BE, used for UI purpose
  additionalUiDetails?: ProductSourceDetails;
}

/** Data part of CS */
export interface DataState {
  /** The product */
  product: ProductType | null;
  /** Flat list of product variants */
  variants: ProductVariantEntities;
  /** Flat list of product sizes */
  sizes: ProductSizeEntities;
  /** Flat list of product prepacks */
  prepacks: ProductPrepackEntities;
  /** Flat list of product scaleSets */
  scaleSets: ScaleSets | null;
  /** Dictionary of prices for the products in the config strip */
  prices: ProductPriceTypes;
  /** delivery shops tab on CS - taken from other State */
  deliveryShops: ExtendedBasketAccountAddress[] | null;
  /** an array of JS functions, which needs to be executed on the sizes */
  validators: string[];
  sizeRuns?: ISizeRuns | null;
}

/**
 * Config strip feature state
 */
export interface State {
  ui: UiState;
  business: BusinessState;
  data: DataState;
}

export type ProductQuantitiesChange = {
  productId: string;
  quantity: number;
  deliveryPeriod: string;
  deliveryPeriodType?: DeliveryPeriodType;
  note?: string | null;
  accountAddressKey: string;
};
